import { computed, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
} from '@ngrx/signals';
import {
  entityConfig,
  setAllEntities,
  withEntities,
} from '@ngrx/signals/entities';
import { UserRecentService } from '@thiememeulenhoff/ng-api-gateway';
import { RecentSubject, UserRecent } from '@thiememeulenhoff/types';

const userRecentEntityConfig = entityConfig({
  entity: {} as UserRecent,
  selectId: (recent) => recent.subjectIdentifier,
});

const sortByCreatedAtDescending = (recents: Array<UserRecent>) =>
  [...recents].sort((a, b) =>
    new Date(a.createdAt) < new Date(b.createdAt) ? 1 : -1,
  );

export const UserRecentStore = signalStore(
  { providedIn: 'root' },
  withEntities(userRecentEntityConfig),
  withComputed((store) => ({
    recentMethods: computed(() =>
      sortByCreatedAtDescending(
        store
          .entities()
          .filter(({ subject }) => subject === RecentSubject.Method),
      ),
    ),
  })),
  withMethods((store, userRecentService = inject(UserRecentService)) => {
    const setRecents = (recents: Array<UserRecent>) =>
      patchState(store, setAllEntities(recents, userRecentEntityConfig));

    const updateRecents = (recents: {
      recentArrangements: Array<UserRecent>;
      recentMethods: Array<UserRecent>;
      recentResources: Array<UserRecent>;
    }) =>
      setRecents([
        ...recents.recentArrangements,
        ...recents.recentMethods,
        ...recents.recentResources,
      ]);

    return {
      setRecents,
      recentArrangements: (methodSlug: string) =>
        computed(() =>
          sortByCreatedAtDescending(
            store
              .entities()
              .filter(
                ({ subject, parentIdentifier }) =>
                  subject === RecentSubject.Arrangement &&
                  parentIdentifier === methodSlug,
              ),
          ),
        ),
      recentResource: (arrangementCode: string) =>
        computed(
          () =>
            store
              .entities()
              .find(
                ({ subject, parentIdentifier }) =>
                  subject === RecentSubject.Resource &&
                  parentIdentifier === arrangementCode,
              ) ?? null,
        ),
      async setRecentMethod(methodSlug: string): Promise<void> {
        updateRecents(
          await firstValueFrom(
            userRecentService.createRecentMethod({
              subjectIdentifier: methodSlug,
            }),
          ),
        );
      },
      async setRecentArrangement(
        methodSlug: string,
        arrangementContentIdentifier: string,
      ): Promise<void> {
        updateRecents(
          await firstValueFrom(
            userRecentService.createRecentArrangement(methodSlug, {
              subjectIdentifier: arrangementContentIdentifier,
            }),
          ),
        );
      },
      async setRecentResource(
        arrangementCode: string,
        contentIdentifier: string,
      ): Promise<void> {
        updateRecents(
          await firstValueFrom(
            userRecentService.createRecentResource(arrangementCode, {
              subjectIdentifier: contentIdentifier,
            }),
          ),
        );
      },
      async removeAllRecents(): Promise<void> {
        await firstValueFrom(userRecentService.removeAllRecents());
        setRecents([]);
      },
    };
  }),
);
